import React, { useCallback, useMemo, useState } from 'react';
import { match } from 'variant';
import kebabCase from 'lodash/kebabCase.js';
import type { SelectGroup } from '@wix/cli-ui-kit';
import {
  Text,
  TextInput,
  SelectInput,
  SelectGroupsInput,
  Spinner,
  Link,
} from '@wix/cli-ui-kit';
import { useDevCenterClient } from '@wix/dev-center-client';
import type {
  WidgetPlugin,
  Placement,
  RequiredApp,
} from '@wix/extensions-data-client';
import {
  getEcomRequiredApps,
  useExtensionDataClient,
} from '@wix/extensions-data-client';
import { getSitePluginExtensionsDir } from '@wix/cli-astro-definitions';
import uniqBy from 'lodash/uniqBy.js';
import {
  createCustomElementFolderValidation,
  useCustomElementNameValidation,
  useValidation,
} from '../../../dev-center/components/validations/index.js';
import { useGenerationTask } from '../hooks/use-generation-task.js';
import { generateSitePlugin } from './generate.js';
import { useProjectModel } from '../../../model.js';
import { useWixCliAstroBi } from '../../../bi/index.js';
import { MultipleAppRequirement } from '../MultipleAppRequirement.js';
import { InstallDependencies } from '../InstallDependencies.js';
import { GenerationSuccessMessage } from '../GenerationSuccessMessage.js';

export const SitePluginWizard: React.FC<{ widgetPlugins: WidgetPlugin[] }> = ({
  widgetPlugins,
}) => {
  const { model } = useProjectModel();
  const { addRequiredApp } = useDevCenterClient();
  const { getWidgetDocsUrl } = useExtensionDataClient();

  const bi = useWixCliAstroBi();
  const validateName = useCustomElementNameValidation();
  const validatePluginFolder = useValidation(
    createCustomElementFolderValidation(
      getSitePluginExtensionsDir(model.projectFolder)
    )
  );

  const [slot, setSlot] = useState<{
    placement: Placement;
    requiredApps: RequiredApp[];
  }>();
  const [requireAppsIds, setRequireAppsIds] = useState<string[]>();
  const [placementApp, setPlacementApp] = useState<string>();
  const [pluginFolder, setPluginFolder] = useState<string>();
  const [pluginName, setPluginName] = useState<string>();
  const [finishedInstallation, setFinishedInstallation] =
    useState<boolean>(false);

  const pluginApplications = useMemo(
    () =>
      uniqBy(widgetPlugins, 'appName').map((pluginPlacement) => ({
        value: pluginPlacement.appName,
        title: pluginPlacement.appName,
      })),
    [widgetPlugins]
  );

  const widgetSlots = useMemo(
    () =>
      placementApp
        ? widgetPlugins
            .filter((widgetPlugin) => widgetPlugin.appName === placementApp)
            .reduce<Record<string, SelectGroup<Placement>>>(
              (_widgetSlots, widgetPlugin) => {
                const widgetDocsUrl = getWidgetDocsUrl(
                  widgetPlugin.component.componentId
                );

                const description = widgetDocsUrl && (
                  <Text skin="secondary">
                    <Link url={widgetDocsUrl}>read more</Link>
                  </Text>
                );

                _widgetSlots[widgetPlugin.component.componentId] ??= {
                  title: widgetPlugin.component.name,
                  description,
                  items: [],
                };

                for (const placement of widgetPlugin.component.data.widgetPlugin
                  .placements) {
                  _widgetSlots[widgetPlugin.component.componentId]?.items.push({
                    key: placement.slotId,
                    title: placement.slotId,
                    value: placement,
                  });
                }

                return _widgetSlots;
              },
              {}
            )
        : {},
    [widgetPlugins, placementApp, getWidgetDocsUrl]
  );

  const extensionType = 'site plugin';
  const defaultPluginFolder = kebabCase(pluginName);

  const generationTask = useGenerationTask(async () => {
    if (pluginName && slot && requireAppsIds) {
      const requireAppId = requireAppsIds.at(0);

      if (requireAppId) {
        await addRequiredApp({
          appId: model.config.appId,
          appIdToRequire: requireAppId,
        });
      }

      const { appDefinitionId, widgetId, slotId } = slot.placement;
      const widgetPlugin = widgetSlots[widgetId];

      if (widgetPlugin) {
        return generateSitePlugin(
          model.projectFolder,
          pluginFolder ?? defaultPluginFolder,
          pluginName,
          [{ appDefinitionId, widgetId, slotId }]
        );
      }
    }
  }, [pluginFolder, pluginName, slot, widgetSlots, getWidgetDocsUrl]);

  const handleSetSlot = useCallback(
    (placement: Placement) => {
      void getEcomRequiredApps().then((ecomRequiredApp) => {
        const widgetPlugin = widgetPlugins.find(
          (widgetPlugin) =>
            widgetPlugin.component.componentId === placement.widgetId
        );

        if (ecomRequiredApp.appId === widgetPlugin?.appId) {
          setSlot({ requiredApps: ecomRequiredApp.requiredApps, placement });
        } else {
          setSlot({
            requiredApps: [
              {
                appId: widgetPlugin!.appId,
                appName: widgetPlugin!.appName,
              },
            ],
            placement,
          });
        }
      });
    },
    [widgetPlugins]
  );

  return (
    <>
      <SelectInput<string>
        label={'Select a Wix app to view its plugin slots'}
        info={
          <Text>
            <Link url="https://wix.to/Onfe4XJ">read more</Link>
          </Text>
        }
        options={pluginApplications}
        onSubmit={(option) => {
          bi.cliFlowStepAnswered({
            question: 'Select a Wix app to view its plugin slots',
            questionKey: 'generate_command.site_plugin.plugin_app',
            answer: option.title,
            additionalInfo: { ...option },
          });
          setPlacementApp(option.value);
        }}
      />
      {placementApp && (
        <SelectGroupsInput<Placement>
          label={'Select the slot in which you’d like to place your plugin'}
          groups={Object.values(widgetSlots)}
          onSubmit={(option) => {
            bi.cliFlowStepAnswered({
              question: 'generate_command.site_plugin.choose_placement',
              questionKey: 'generate_command.site_plugin.choose_placement',
              answer: option.title,
              additionalInfo: option.value,
            });
            handleSetSlot(option.value);
          }}
        />
      )}
      {slot?.requiredApps && (
        <MultipleAppRequirement
          appId={model.config.appId}
          requiredApps={slot.requiredApps.map((requiredApp) => ({
            id: requiredApp.appId,
            name: requiredApp.appName,
          }))}
          onSubmit={setRequireAppsIds}
        />
      )}
      {requireAppsIds && (
        <TextInput
          label={'Enter a name for your site plugin:'}
          placeholder={'My Site Plugin'}
          onSubmit={(value) => {
            bi.cliFlowStepAnswered({
              question: 'Enter a name for your site plugin:',
              questionKey: 'generate_command.site_plugin.choose_name',
              answer: value,
              additionalInfo: {
                extensionType,
              },
            });
            setPluginName(value);
          }}
          validate={validateName}
        />
      )}
      {pluginName && (
        <TextInput
          label={'Enter a folder name for your site plugin:'}
          placeholder={defaultPluginFolder}
          onSubmit={(value) => {
            const finalValue = value || defaultPluginFolder;
            bi.cliFlowStepAnswered({
              question: 'Enter a folder name for your site plugin:',
              questionKey: 'generate_command.site_plugin.choose_folder',
              answer: finalValue,
              additionalInfo: {
                extensionType,
              },
            });
            setPluginFolder(finalValue);
          }}
          validate={validatePluginFolder}
        />
      )}
      {useMemo(
        () =>
          match(generationTask, {
            Success: ({ hasUpdatedDependencies, newFiles }) => {
              if (!hasUpdatedDependencies) {
                setFinishedInstallation(true);
              }
              return (
                <>
                  {hasUpdatedDependencies && (
                    <InstallDependencies
                      onFinish={() => setFinishedInstallation(true)}
                    />
                  )}
                  {finishedInstallation && (
                    <GenerationSuccessMessage
                      extensionType={extensionType}
                      generatedFiles={newFiles}
                      // https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/site-plugins/add-a-site-plugin-extension
                      learnMoreUrl="https://wix.to/lV3F6Fc"
                    />
                  )}
                </>
              );
            },
            Loading: () => <Spinner text={`Generating ${extensionType}...`} />,
            Error: () => null,
            NotRequested: () => null,
          }),
        [
          generationTask,
          extensionType,
          finishedInstallation,
          setFinishedInstallation,
        ]
      )}
    </>
  );
};
